import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../pages/GuestDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function GuestResourcesPanel() {
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubjectId, setExpandedSubjectId] = useState(null);
  const [summaryResource, setSummaryResource] = useState(null);
  const [summaryContent, setSummaryContent] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [semesterFilter, setSemesterFilter] = useState('All');

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/subject/public`);
        const data = await res.json();
        if (res.ok) {
          setSubjects(data);
          if (data.length > 0) setExpandedSubjectId(data[0]._id);
        } else {
          console.error('Failed to load public subjects', data);
          setSubjects([]);
        }
      } catch (err) {
        console.error('Error fetching public subjects', err);
        setSubjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  const handleToggleSubject = (subjectId) => {
    setExpandedSubjectId(prev => (prev === subjectId ? null : subjectId));
  };

  const openDocument = (res) => {
    const fileUrl = res.url && res.url.startsWith('http') ? res.url : `${API_BASE}${res.url}`;
    window.open(fileUrl, '_blank', 'noopener');
  };

  const openSummary = async (resource) => {
    setSummaryResource(resource);
    setSummaryContent(null);
    setLoadingSummary(true);
    try {
      const res = await fetch(`${API_BASE}/resource/public/item/${resource._id}/summary`);
      const data = await res.json();
      if (res.ok) setSummaryContent(data.content || 'No summary available.');
      else setSummaryContent(data.message || 'Failed to load summary.');
    } catch (err) {
      console.error('Error loading summary', err);
      setSummaryContent('Network error loading summary.');
    } finally {
      setLoadingSummary(false);
    }
  };

  return (
    <div className="guest-resources-panel">
      <h3>Public Resources</h3>
      <p className="muted">Publicly visible subject resources — useful for quick previews</p>

      {loading ? (
        <div className="guest-loading">
          <div className="spinner"></div>
          <p>Loading MCA subjects...</p>
        </div>
      ) : subjects.length === 0 ? (
        <div className="no-subjects-card">No public subjects found.</div>
      ) : (
        <>
          {/* Semester filter (matches Guest Dashboard) */}
          <div className="guest-controls">
            <div className="semester-tabs">
              <button
                className={`sem-tab ${semesterFilter === 'All' ? 'active' : ''}`}
                onClick={() => setSemesterFilter('All')}
              >
                All
              </button>
              {Array.from(new Set(subjects.map(s => s.semester || 'Unknown'))).map(sem => (
                <button
                  key={sem}
                  className={`sem-tab ${semesterFilter === sem ? 'active' : ''}`}
                  onClick={() => setSemesterFilter(sem)}
                >
                  {sem}
                </button>
              ))}
            </div>
          </div>

          <div className="subjects-grid">
            {subjects
              .filter(s => semesterFilter === 'All' || (s.semester || 'Unknown') === semesterFilter)
              .map(subject => {
                const expanded = expandedSubjectId === subject._id;
                const resourceCount = subject.resources ? subject.resources.length : 0;
                return (
                  <div key={subject._id} className={`guest-subject-card ${expanded ? 'expanded' : ''}`}>
                    <div
                      className="subject-card-header"
                      onClick={() => handleToggleSubject(subject._id)}
                    >
                      <div className="subject-header-info">
                        <span className="sem-badge">{subject.semester || 'MCA Sem 1'}</span>
                        <h2>{subject.name}</h2>
                        <span className="resource-count">📖 {resourceCount} {resourceCount === 1 ? 'Resource' : 'Resources'}</span>
                      </div>
                      <button className="expand-toggle-btn">{expanded ? '▲ Hide' : '▼ View Files'}</button>
                    </div>

                    {expanded && (
                      <div className="subject-resources-container">
                        {resourceCount === 0 ? (
                          <p className="empty-resources-msg">No PDF or study material attached to this subject yet.</p>
                        ) : (
                          <div className="guest-resource-list">
                            {subject.resources.map((res) => {
                              return (
                                <div key={res._id} className="guest-resource-item">
                                  <div className="res-file-details">
                                    <span className="res-type-icon">{res.type === 'pdf' ? '📕' : res.type === 'docx' ? '📘' : '📄'}</span>
                                    <div className="res-name-block">
                                      <span className="res-filename">{res.name}</span>
                                      <span className="res-meta">{res.size ? (res.size/1024).toFixed(1) : '0.0'} KB • {res.type?.toUpperCase()}</span>
                                    </div>
                                  </div>

                                  <div className="res-action-buttons">
                                    <button
                                      className="btn-action view-pdf-btn"
                                      onClick={() => openDocument(res)}
                                    >
                                      📖 View Document
                                    </button>

                                    {res.hasSummary ? (
                                      <button
                                        className="btn-action view-summary-btn"
                                        onClick={() => openSummary(res)}
                                      >
                                        ✨ AI Summary
                                      </button>
                                    ) : (
                                      <button className="btn-action view-summary-btn" disabled title="Summary not generated">
                                        🔒 Summary Locked
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}

      {/* Summary modal */}
      {summaryResource && (
        <div className="guest-modal-overlay" onClick={() => setSummaryResource(null)}>
          <div className="guest-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-box">
                <span className="modal-icon">✨</span>
                <h3>AI Summary - {summaryResource.name}</h3>
              </div>
              <button className="modal-close-btn" onClick={() => setSummaryResource(null)}>×</button>
            </div>

            <div className="modal-body summary-body">
              {loadingSummary ? (
                <div className="summary-loading">
                  <div className="spinner"></div>
                  <p>Generating/fetching AI summary for this document...</p>
                </div>
              ) : (
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {summaryContent || 'No summary available.'}
                  </ReactMarkdown>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}
